// Import ThumbmarkJS from the npm package
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';

// Show error message when fingerprint cannot be captured
function showFingerprintError(form) {
  // Remove any existing error messages
  const existingError = form.querySelector('.fingerprint-error');
  if (existingError) {
    existingError.remove();
  }
  
  // Create error message element
  const errorDiv = document.createElement('div');
  errorDiv.className = 'fingerprint-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6';
  errorDiv.innerHTML = `
    <div class="flex items-start">
      <svg class="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <div>
        <h4 class="font-medium">Security verification failed</h4>
        <p class="mt-1 text-sm">
          We're unable to verify your device for security purposes. This is often caused by:
        </p>
        <ul class="mt-2 list-disc list-inside text-sm">
          <li>Ad blockers or content blockers (uBlock, AdBlock, etc.)</li>
          <li>Privacy-focused browser extensions</li>
          <li>Strict browser privacy settings</li>
          <li>VPN or proxy interference</li>
        </ul>
        <p class="mt-2 text-sm font-medium">
          Please disable any content blockers for this domain and try again.
        </p>
      </div>
    </div>
  `;
  
  // Insert error message at the top of the form
  const firstChild = form.firstElementChild;
  if (firstChild) {
    form.insertBefore(errorDiv, firstChild);
  } else {
    form.appendChild(errorDiv);
  }
  
  // Scroll to error message
  errorDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

// Get visitor fingerprint using ThumbmarkJS
export async function getVisitorFingerprint() {
  try {
    // ThumbmarkJS returns a string hash directly
    const fingerprint = await getFingerprint();
    
    const result = {
      visitorId: fingerprint,
      // ThumbmarkJS doesn't provide confidence score or components
      // but we can add metadata
      timestamp: new Date().getTime(), // Use milliseconds timestamp
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      library: 'thumbmarkjs'
    };
    
    return result;
  } catch (error) {
    console.error('[Fingerprint] Failed to get thumbmark:', error);
    return null;
  }
}

// Attach fingerprint to form submission
export function attachFingerprintToForm(formId, fieldId) {
  const form = document.getElementById(formId);
  const field = document.getElementById(fieldId);
  
  if (!form || !field) {
    return;
  }
  
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    // Update submit button state
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    const originalButtonText = submitButton ? (submitButton.value || submitButton.textContent) : '';
    
    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.value) {
        submitButton.value = 'Verifying security...';
      } else {
        submitButton.textContent = 'Verifying security...';
      }
    }
    
    try {
      const fingerprintData = await getVisitorFingerprint();
      
      if (fingerprintData && fingerprintData.visitorId) {
        // Store the full fingerprint data as JSON
        const jsonValue = JSON.stringify(fingerprintData);
        field.value = jsonValue;
        
        // Update button to show processing
        if (submitButton) {
          if (submitButton.value) {
            submitButton.value = 'Processing...';
          } else {
            submitButton.textContent = 'Processing...';
          }
        }
        
        // Submit the form
        form.submit();
      } else {
        // Fingerprint capture failed - show error
        showFingerprintError(form);
        
        // Re-enable submit button
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.value) {
            submitButton.value = originalButtonText;
          } else {
            submitButton.textContent = originalButtonText;
          }
        }
      }
    } catch (error) {
      console.error('[Fingerprint] Error getting fingerprint:', error);
      
      // Show error message
      showFingerprintError(form);
      
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.value) {
          submitButton.value = originalButtonText;
        } else {
          submitButton.textContent = originalButtonText;
        }
      }
    }
  });
}

// Check if fingerprinting is available on page load
async function checkFingerprintingAvailable() {
  try {
    const testFingerprint = await getFingerprint();
    return testFingerprint && testFingerprint.length > 0;
  } catch (error) {
    return false;
  }
}

// Show warning banner if fingerprinting is blocked
function showFingerprintWarning() {
  // Create warning banner
  const warningDiv = document.createElement('div');
  warningDiv.className = 'fingerprint-warning bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 mb-6';
  warningDiv.innerHTML = `
    <div class="flex items-start">
      <svg class="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>
      <div>
        <h4 class="font-medium">Security verification may be blocked</h4>
        <p class="mt-1 text-sm">
          We detected that security verification might be blocked by your browser or extensions. 
          You may need to disable content blockers to complete this form.
        </p>
      </div>
    </div>
  `;
  
  // Find forms and insert warning
  const forms = document.querySelectorAll('#contract-signing-form, #facilitator-application-form');
  forms.forEach(form => {
    const existingWarning = form.querySelector('.fingerprint-warning');
    if (!existingWarning) {
      const firstChild = form.firstElementChild;
      if (firstChild) {
        form.insertBefore(warningDiv.cloneNode(true), firstChild);
      }
    }
  });
}

// Auto-initialize if DOM is ready
async function initializeForms() {
  // Check if fingerprinting is available
  const fingerprintAvailable = await checkFingerprintingAvailable();
  
  if (!fingerprintAvailable) {
    showFingerprintWarning();
  }
  
  // Look for contract signing forms
  const contractForm = document.getElementById('contract-signing-form');
  if (contractForm) {
    attachFingerprintToForm('contract-signing-form', 'fingerprint-field');
  }
  
  // Look for facilitator application forms
  const facilitatorForm = document.getElementById('facilitator-application-form');
  if (facilitatorForm) {
    attachFingerprintToForm('facilitator-application-form', 'fingerprint-field');
  }
}

// Initialize on various events to ensure we catch the form
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeForms();
  });
} else {
  // DOM is already ready
  initializeForms();
}

// Also try with Turbo events since Rails 7 uses Turbo
document.addEventListener('turbo:load', () => {
  initializeForms();
});

document.addEventListener('turbo:frame-load', () => {
  initializeForms();
});